import { useCallback, useState } from 'react';
import { Avatar } from 'avatar-components/Avatar';
import http from 'conversations-http/clients/http';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Link } from 'foundations-components/transitional/components/Link';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Small } from 'foundations-components/transitional/components/Small';
import FormattedMessage from 'I18n/components/FormattedMessage';
import formatName from 'I18n/utils/formatName';
import { useUserInfo } from '../../../auth/hooks/useUserInfo';
import { HELPDESK } from '../../../shared/constants/workspaces';
import { linkToUsersAndTeamSettings } from '../../../urls/linkToUsersAndTeamSettings';
import { getUserWorkingHoursURL } from '../../../working-hours/utils/workingHoursUtils';
import { checkIsNullish } from '../../utils/capacityUtils';
import AvailabilityIndicator from '../AvailabilityIndicator';
import { OutOfOfficeDetails } from '../StatusMessage/OutOfOfficeLabel';
import { CapacitySection } from './CapacitySection';
import { PopoverActionButton } from './PopoverActionButton';
import { PortalCapacitySection } from './PortalCapacitySection';
import { useIsUngatedForRoutingAwayStatusTimer } from '../../../auth/hooks/useGates';
import { ManageStatusModal } from './ManageStatusModal';
import { useAwayStatusTimer } from '../../../working-hours/hooks/useAwayStatusTimer';
import { AVAILABLE, AWAY } from '../../../reassignment/constants/agent-state';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { AWAY_STATUS_TIMER_ALERT_TIMEOUT } from '../../utils/statusModalUtils';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PopoverMenuItem = ({
  isLast = false,
  padding,
  children
}) => {
  const borderStyle = !isLast && {
    borderBlockEndColor: 'border-core-subtle',
    borderBlockEndStyle: 'solid',
    borderBlockEndWidth: '100'
  };
  return /*#__PURE__*/_jsx(ListItem, {
    children: /*#__PURE__*/_jsx(View, Object.assign({
      paddingInline: "500",
      paddingBlock: "300",
      padding: padding
    }, borderStyle, {
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        children: children
      })
    }))
  });
};
PopoverMenuItem.displayName = 'PopoverMenuItem';
export const AvailabilityPopover = ({
  capacity,
  currentSegment,
  isAway,
  onAwayModeToggle,
  portalCapacityLimit,
  portalCapacityLoad,
  workspace
}) => {
  const user = useUserInfo();
  const isUngatedForRoutingAwayStatusTimer = useIsUngatedForRoutingAwayStatusTimer();
  const fullName = formatName({
    firstName: user === null || user === void 0 ? void 0 : user.user.first_name,
    lastName: user === null || user === void 0 ? void 0 : user.user.last_name
  });
  const isOOO = !!currentSegment;
  const [isManageStatusModalOpen, setIsManageStatusModalOpen] = useState(false);
  const {
    updateAwayStatusTimer,
    isUpdating
  } = useAwayStatusTimer((user === null || user === void 0 ? void 0 : user.user.user_id) || 0, {
    isViewingSelf: true
  });
  const createSuccessAlert = (durationMins, status) => {
    FloatingAlertStore.addAlert({
      type: 'success',
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "routing-ui.agent-status.statusModal.alerts.success.title"
      }),
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "routing-ui.agent-status.statusModal.alerts.success.description",
        options: {
          count: durationMins,
          status: status.toLowerCase()
        }
      }),
      timeout: AWAY_STATUS_TIMER_ALERT_TIMEOUT
    });
  };
  const createErrorAlert = () => {
    FloatingAlertStore.addAlert({
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "routing-ui.agent-status.statusModal.alerts.error.title"
      }),
      type: 'danger',
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "routing-ui.agent-status.statusModal.alerts.error.description"
      }),
      timeout: AWAY_STATUS_TIMER_ALERT_TIMEOUT
    });
  };
  const handleSave = useCallback(async (status, durationMins) => {
    if (status === AVAILABLE) {
      onAwayModeToggle();
      setIsManageStatusModalOpen(false);
      return;
    }
    if (status === AWAY && durationMins) {
      try {
        await updateAwayStatusTimer({
          durationMins,
          timerStartedAt: Date.now()
        });
        createSuccessAlert(durationMins, status);
        onAwayModeToggle();
        setIsManageStatusModalOpen(false);
      } catch (error) {
        Raven.captureException(error);
        createErrorAlert();
      }
    }
  }, [updateAwayStatusTimer, onAwayModeToggle]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(List, {
      children: [/*#__PURE__*/_jsx(PopoverMenuItem, {
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "start",
          children: [/*#__PURE__*/_jsx(View, {
            paddingInlineEnd: "200",
            children: /*#__PURE__*/_jsx(Avatar, {
              size: "sm",
              hubSpotUserEmail: user === null || user === void 0 ? void 0 : user.user.email,
              fetchAvatars: (url, data) => {
                return http.post(url, {
                  data
                });
              }
            })
          }), /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            children: [/*#__PURE__*/_jsx(Text, {
              as: "h6",
              typography: "heading-50",
              children: fullName
            }), isOOO && /*#__PURE__*/_jsx(Small, {
              children: /*#__PURE__*/_jsx(Flex, {
                children: /*#__PURE__*/_jsx(OutOfOfficeDetails, {
                  startTimestamp: currentSegment.startTimestamp,
                  endTimestamp: currentSegment.endTimestamp
                })
              })
            }), workspace !== HELPDESK &&
            // portalCapacityLimit and portalCapacityLoad are only used for PortalCapacitySection,
            // and only defined in Inbox
            !checkIsNullish(portalCapacityLoad) && !checkIsNullish(portalCapacityLimit) && /*#__PURE__*/_jsx(PortalCapacitySection, {
              portalCapacityLoad: portalCapacityLoad,
              portalCapacityLimit: portalCapacityLimit
            }), workspace === HELPDESK && !isOOO && capacity && /*#__PURE__*/_jsx(CapacitySection, {
              userCapacity: capacity
            })]
          })]
        })
      }), /*#__PURE__*/_jsxs(PopoverMenuItem, {
        padding: "0",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "small",
          paddingInline: "500",
          paddingBlockStart: "300",
          paddingBlockEnd: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "routing-ui.agent-status.statusPopover.automateStatus.header"
          })
        }), /*#__PURE__*/_jsx(Link, {
          flexWrap: "nowrap",
          use: "unstyled",
          href: user ? getUserWorkingHoursURL(user.user.user_id) : linkToUsersAndTeamSettings(),
          children: /*#__PURE__*/_jsx(PopoverActionButton, {
            role: "link",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "routing-ui.agent-status.statusPopover.automateStatus.actionLink"
            })
          })
        })]
      }), /*#__PURE__*/_jsxs(PopoverMenuItem, {
        padding: "0",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "small",
          paddingInline: "500",
          paddingBlockStart: "300",
          paddingBlockEnd: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "routing-ui.agent-status.statusPopover.updateStatus.header"
          })
        }), /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsx(PopoverActionButton, {
            onClick: () => onAwayModeToggle(),
            "data-test-id": "availability-toggle-button",
            children: /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              children: [/*#__PURE__*/_jsx(AvailabilityIndicator, {
                atCapacity: false,
                isAwayMode: !isAway
              }), isAway ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: "routing-ui.agent-status.statusPopover.updateStatus.actionButtonAvailable"
              }) : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "routing-ui.agent-status.statusPopover.updateStatus.actionButtonAway"
              })]
            })
          }), isUngatedForRoutingAwayStatusTimer && /*#__PURE__*/_jsx(PopoverActionButton, {
            onClick: () => {
              setIsManageStatusModalOpen(true);
            },
            "data-test-id": "availability-toggle-temporary-button",
            children: /*#__PURE__*/_jsxs(Flex, {
              alignItems: "center",
              children: [/*#__PURE__*/_jsx(AvailabilityIndicator, {
                atCapacity: false,
                isAwayMode: true
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                message: "routing-ui.agent-status.statusPopover.updateStatus.actionButtonAwayTemporarily"
              })]
            })
          })]
        })]
      })]
    }), isUngatedForRoutingAwayStatusTimer && isManageStatusModalOpen && /*#__PURE__*/_jsx(ManageStatusModal, {
      onCancel: () => setIsManageStatusModalOpen(false),
      onSave: handleSave,
      isSaving: isUpdating,
      currentStatus: isAway ? 'away' : 'available'
    })]
  });
};
AvailabilityPopover.displayName = 'AvailabilityPopover';